import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getServices, type PurchasePackage } from '@breath-lab/services';
import { isPremiumTrial, isPurchaseIdentityError, useSubscription } from '@breath-lab/state';
import { useTrialState } from '@breath-lab/state';
import Button from '@ui/components/Button/Button';
import AccessBadge from '@ui/components/AccessBadge/AccessBadge';
import { logEvent } from '../../../infra/firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from '../../../domain/analytics';
import styles from './UpgradePanel.module.css';

function monthlyEquivalent(pkg: PurchasePackage, months: number): string | null {
  const amount = pkg.price.amountMicros;
  const currency = pkg.price.currency ?? 'USD';
  if (!amount) return null;
  const perMonth = amount / months / 1_000_000;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(perMonth);
}

type Props = {
  onComplete: () => void;
};

export default function UpgradePanel({ onComplete }: Props) {
  const { t } = useTranslation('upgrade');
  const { currentOffering, isLoading, purchasePackage } = useSubscription();
  const { isInTrial, trialDaysLeft } = useTrialState();

  const annualPkg =
    currentOffering?.availablePackages.find(
      (p) => p.packageType === 'annual'
    ) ?? null;
  const monthlyPkg =
    currentOffering?.availablePackages.find(
      (p) => p.packageType === 'monthly'
    ) ?? null;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [purchasingPackage, setPurchasingPackage] = useState<string | null>(null);
  const [offerError, setOfferError] = useState<string | null>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (offerError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [offerError]);

  const packages = [annualPkg, monthlyPkg].filter(Boolean) as PurchasePackage[];
  const selectedPkg = packages.find((p) => p.identifier === selectedId) ?? null;

  useEffect(() => {
    if (annualPkg && !selectedId) setSelectedId(annualPkg.identifier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annualPkg?.identifier]);

  useEffect(() => {
    logEvent(AnalyticsEvent.screenView, { [AnalyticsParam.screenName]: 'upgrade' });
  }, []);

  const trialStatus =
    isInTrial && trialDaysLeft !== null
      ? trialDaysLeft === 1
        ? t('trialStatus.lastDay')
        : t('trialStatus.daysLeft', { days: trialDaysLeft })
      : null;

  const ctaLabel = purchasingPackage
    ? t('cta.processing')
    : isInTrial
    ? t('cta.continuePremium')
    : t('cta.subscribe');

  async function handlePurchase() {
    if (!selectedPkg) return;
    logEvent(AnalyticsEvent.purchaseInitiated, {
      [AnalyticsParam.packageId]: selectedPkg.identifier,
    });
    setPurchasingPackage(selectedPkg.identifier);
    setOfferError(null);
    try {
      // Identity-gated: refuses to transact while RC is anonymous.
      const info = await purchasePackage(selectedPkg);
      const isTrial = isPremiumTrial(info);
      logEvent(
        isTrial ? AnalyticsEvent.trialStarted : AnalyticsEvent.purchaseCompleted,
        {
          [AnalyticsParam.packageId]: selectedPkg.identifier,
          ...(isTrial ? { [AnalyticsParam.trialType]: 'store' } : {}),
        },
      );
      onComplete();
    } catch (e: unknown) {
      if (getServices().purchases.errorCodeOf(e) === 'user_cancelled') {
        logEvent(AnalyticsEvent.purchaseCancelled, {
          [AnalyticsParam.packageId]: selectedPkg.identifier,
        });
      } else {
        logEvent(AnalyticsEvent.purchaseFailed, {
          [AnalyticsParam.packageId]: selectedPkg.identifier,
        });
        setOfferError(
          isPurchaseIdentityError(e)
            ? t('errors.accountNotVerified')
            : t('errors.purchaseFailed'),
        );
      }
    } finally {
      setPurchasingPackage(null);
    }
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <AccessBadge variant="premium">{t('premiumBadge')}</AccessBadge>
        <h1 className={styles.title}>{t('title')}</h1>
        {trialStatus && <p className={styles.trialStatus}>{trialStatus}</p>}
      </div>

      <ul className={styles.benefits}>
        <li className={styles.benefitItem}>
          <span className={styles.benefitTitle}>{t('benefits.allTechniques.title')}</span>
          <span className={styles.benefitDesc}>{t('benefits.allTechniques.desc')}</span>
        </li>
        <li className={styles.benefitItem}>
          <span className={styles.benefitTitle}>{t('benefits.fullGuides.title')}</span>
          <span className={styles.benefitDesc}>{t('benefits.fullGuides.desc')}</span>
        </li>
        <li className={styles.benefitItem}>
          <span className={styles.benefitTitle}>{t('benefits.allJourneys.title')}</span>
          <span className={styles.benefitDesc}>{t('benefits.allJourneys.desc')}</span>
        </li>
      </ul>

      {isLoading && <p className={styles.loading}>{t('loadingOptions')}</p>}

      {/* RC unconfigured (configure failed at login) or offerings fetch failed —
          show an explicit error instead of a silently empty panel. */}
      {!isLoading && packages.length === 0 && (
        <p className={styles.error}>{t('errors.couldNotLoad')}</p>
      )}

      {!isLoading && packages.length > 0 && (
        <>
          <div className={styles.packages}>
            {packages.map((pkg) => {
              const isSelected = pkg.identifier === selectedId;
              const isAnnual = pkg.packageType === 'annual';
              const perMonth = isAnnual ? monthlyEquivalent(pkg, 12) : null;

              return (
                <button
                  key={pkg.identifier}
                  className={`${styles.packageCard} ${isSelected ? styles.packageCardSelected : ''}`}
                  onClick={() => {
                    setSelectedId(pkg.identifier);
                    setOfferError(null);
                  }}
                  disabled={purchasingPackage !== null}
                  type="button"
                >
                  <div className={styles.packageLeft}>
                    <span className={styles.packageName}>
                      {isAnnual ? t('plans.annual') : t('plans.monthly')}
                    </span>
                    {isAnnual && (
                      <span className={styles.bestValueBadge}>{t('plans.bestValue')}</span>
                    )}
                    {isAnnual && (
                      <span className={styles.billedNote}>{t('plans.billedAnnually')}</span>
                    )}
                  </div>
                  <div className={styles.packageRight}>
                    <span className={styles.packagePrice}>
                      {pkg.price.formattedPrice}
                    </span>
                    {perMonth && (
                      <span className={styles.perMonth}>
                        {t('plans.perMonthRate', { price: perMonth })}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <Button
            variant="primary"
            size="lg"
            className={styles.cta}
            onClick={handlePurchase}
            disabled={purchasingPackage !== null || !selectedPkg}
          >
            {ctaLabel}
          </Button>

          <p className={styles.cancelNote}>{t('cancelNote')}</p>
        </>
      )}

      {offerError && <p ref={errorRef} className={styles.error}>{offerError}</p>}
    </div>
  );
}
